/**
 * Edit Quiz Screen
 * Edit quiz settings for live (Kahoot-style) quizzes
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";

/**
 * Available icons
 */
const AVAILABLE_ICONS = [
  "help-circle",
  "bulb",
  "school",
  "book",
  "trophy",
  "star",
  "flash",
  "rocket",
  "planet",
  "musical-notes",
  "film",
  "football",
  "heart",
  "flag",
  "globe",
];

/**
 * Available colors
 */
const AVAILABLE_COLORS = [
  "#3B82F6",
  "#8B5CF6",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#6366F1",
  "#14B8A6",
  "#EF4444",
  "#84CC16",
  "#F97316",
];

export default function EditQuizScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { isDark } = useTheme();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [questionsCount, setQuestionsCount] = useState(0);
  const [sectionsCount, setSectionsCount] = useState(0);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("help-circle");
  const [color, setColor] = useState("#3B82F6");
  const [timePerQuestion, setTimePerQuestion] = useState(30);
  const [pointsPerCorrect, setPointsPerCorrect] = useState(100);
  const [bonusSpeed, setBonusSpeed] = useState(50);
  const [randomize, setRandomize] = useState(true);
  const [isActive, setIsActive] = useState(true);

  /**
   * Calculate max points per question
   */
  const maxPointsPerQuestion = pointsPerCorrect + bonusSpeed;

  /**
   * Fetch quiz data
   */
  const fetchQuiz = useCallback(async () => {
    // Fetch quiz
    const { data, error } = await supabase
      .from("quizzes")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      Alert.alert("Fout", "Kon quiz niet laden");
      router.back();
      return;
    }

    // Fetch question count
    const { count: qCount } = await supabase
      .from("quiz_questions")
      .select("*", { count: "exact", head: true })
      .eq("quiz_id", id);

    // Fetch section count
    const { count: sCount } = await supabase
      .from("quiz_sections")
      .select("*", { count: "exact", head: true })
      .eq("quiz_id", id);

    setName(data.name);
    setDescription(data.description || "");
    setIcon(data.icon);
    setColor(data.color);
    setTimePerQuestion(data.time_per_question);
    setPointsPerCorrect(data.points_per_correct);
    setBonusSpeed(data.bonus_speed);
    setRandomize(data.randomize_questions);
    setIsActive(data.is_active);
    setQuestionsCount(qCount || 0);
    setSectionsCount(sCount || 0);
    setIsLoading(false);
  }, [id, router]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Fout", "Vul een naam in");
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("quizzes")
        .update({
          name: name.trim(),
          description: description.trim() || null,
          icon,
          color,
          time_per_question: timePerQuestion,
          points_per_correct: pointsPerCorrect,
          bonus_speed: bonusSpeed,
          randomize_questions: randomize,
          is_active: isActive,
          // Always set live mode flags
          is_live_mode: true,
          is_team_based: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) {
        throw new Error(error.message);
      }

      Alert.alert("Succes", "Quiz is bijgewerkt!", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (error) {
      console.error("Error updating quiz:", error);
      Alert.alert("Fout", "Kon quiz niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="arrow-back" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Quiz Bewerken
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl"
            style={{ backgroundColor: isSaving ? colors.border : colors.primary }}
            accessibilityRole="button"
            accessibilityLabel="Opslaan"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text className="font-bold text-white">Opslaan</Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView
          className="flex-1 px-4"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Quiz Stats */}
          <View
            className="rounded-xl p-4 mb-4 flex-row"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-1 items-center">
              <Text className="text-2xl font-bold" style={{ color: color }}>
                {questionsCount}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Vragen
              </Text>
            </View>
            <View className="w-px" style={{ backgroundColor: colors.border }} />
            <View className="flex-1 items-center">
              <Text className="text-2xl font-bold" style={{ color: color }}>
                {sectionsCount}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Secties
              </Text>
            </View>
            <View className="w-px" style={{ backgroundColor: colors.border }} />
            <View className="flex-1 items-center">
              <Text className="text-2xl font-bold" style={{ color: "#F59E0B" }}>
                {questionsCount * maxPointsPerQuestion}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Max Punten
              </Text>
            </View>
          </View>

          {/* Active Toggle */}
          <View
            className="rounded-xl p-4 mb-4 flex-row items-center justify-between"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center">
              <Ionicons
                name={isActive ? "eye" : "eye-off"}
                size={20}
                color={isActive ? "#10B981" : colors.error}
              />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Quiz actief
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => setIsActive(!isActive)}
              className="w-14 h-8 rounded-full justify-center px-1"
              style={{ backgroundColor: isActive ? "#10B981" : colors.border }}
              accessibilityRole="switch"
              accessibilityState={{ checked: isActive }}
            >
              <View
                className="w-6 h-6 rounded-full bg-white"
                style={{ alignSelf: isActive ? "flex-end" : "flex-start" }}
              />
            </TouchableOpacity>
          </View>

          {/* Preview */}
          <View className="items-center py-4">
            <View
              className="w-16 h-16 rounded-2xl items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Ionicons name={icon as any} size={32} color={color} />
            </View>
          </View>

          {/* Name */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Naam *
            </Text>
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder="Quiz naam"
              placeholderTextColor={colors.textTertiary}
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
              }}
              accessibilityLabel="Quiz naam"
            />
          </View>

          {/* Description */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Beschrijving
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Beschrijving"
              placeholderTextColor={colors.textTertiary}
              multiline
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                minHeight: 70,
                textAlignVertical: "top",
              }}
              accessibilityLabel="Quiz beschrijving"
            />
          </View>

          {/* Icon Selection */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Icoon
            </Text>
            <View className="flex-row flex-wrap">
              {AVAILABLE_ICONS.map((iconName) => (
                <TouchableOpacity
                  key={iconName}
                  onPress={() => setIcon(iconName)}
                  className="w-11 h-11 rounded-lg items-center justify-center m-1"
                  style={{
                    backgroundColor: icon === iconName ? `${color}30` : colors.surface,
                    borderWidth: 2,
                    borderColor: icon === iconName ? color : colors.border,
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: icon === iconName }}
                >
                  <Ionicons
                    name={iconName as any}
                    size={20}
                    color={icon === iconName ? color : colors.textSecondary}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Color Selection */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Kleur
            </Text>
            <View className="flex-row flex-wrap">
              {AVAILABLE_COLORS.map((colorOption) => (
                <TouchableOpacity
                  key={colorOption}
                  onPress={() => setColor(colorOption)}
                  className="w-11 h-11 rounded-lg items-center justify-center m-1"
                  style={{
                    backgroundColor: colorOption,
                    borderWidth: 3,
                    borderColor: color === colorOption ? colors.text : "transparent",
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: color === colorOption }}
                >
                  {color === colorOption && (
                    <Ionicons name="checkmark" size={18} color="#FFFFFF" />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Live Quiz Settings */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
              Live Quiz Instellingen
            </Text>

            <View className="rounded-xl p-4" style={{ backgroundColor: colors.surface }}>
              {/* Time per Question */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#3B82F620" }}
                  >
                    <Ionicons name="time" size={20} color="#3B82F6" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Tijd per vraag</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Seconden om te antwoorden
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setTimePerQuestion(Math.max(10, timePerQuestion - 5))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={16} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-3 font-bold text-lg"
                    style={{ color: colors.text, minWidth: 40, textAlign: "center" }}
                  >
                    {timePerQuestion}s
                  </Text>
                  <TouchableOpacity
                    onPress={() => setTimePerQuestion(Math.min(120, timePerQuestion + 5))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={16} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Points per Correct */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#10B98120" }}
                  >
                    <Ionicons name="star" size={20} color="#10B981" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Basispunten</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Punten voor goed antwoord
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setPointsPerCorrect(Math.max(10, pointsPerCorrect - 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={16} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-3 font-bold text-lg"
                    style={{ color: "#10B981", minWidth: 50, textAlign: "center" }}
                  >
                    {pointsPerCorrect}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setPointsPerCorrect(Math.min(1000, pointsPerCorrect + 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={16} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Bonus Speed */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#F59E0B20" }}
                  >
                    <Ionicons name="flash" size={20} color="#F59E0B" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Snelheidsbonus</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Max bonus voor snelste antwoord
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setBonusSpeed(Math.max(0, bonusSpeed - 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={16} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-3 font-bold text-lg"
                    style={{ color: "#F59E0B", minWidth: 50, textAlign: "center" }}
                  >
                    +{bonusSpeed}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setBonusSpeed(Math.min(500, bonusSpeed + 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={16} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Max Points Info */}
              <View
                className="p-3 rounded-lg flex-row items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="trophy" size={18} color="#F59E0B" />
                <Text className="ml-2 text-sm" style={{ color: colors.textSecondary }}>
                  Max {maxPointsPerQuestion} punten per vraag
                </Text>
              </View>
            </View>
          </View>

          {/* Randomize Option */}
          <View className="mb-4">
            <View
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface }}
            >
              <TouchableOpacity
                onPress={() => setRandomize(!randomize)}
                className="flex-row items-center justify-between"
                accessibilityRole="switch"
                accessibilityState={{ checked: randomize }}
              >
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#8B5CF620" }}
                  >
                    <Ionicons name="shuffle" size={20} color="#8B5CF6" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Willekeurige volgorde</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Vragen in willekeurige volgorde tonen
                    </Text>
                  </View>
                </View>
                <View
                  className="w-14 h-8 rounded-full justify-center px-1"
                  style={{ backgroundColor: randomize ? "#8B5CF6" : colors.border }}
                >
                  <View
                    className="w-6 h-6 rounded-full bg-white"
                    style={{ alignSelf: randomize ? "flex-end" : "flex-start" }}
                  />
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {/* Live Quiz Info */}
          <View
            className="rounded-xl p-4 mb-4"
            style={{
              backgroundColor: isDark ? "#1E3A5F" : "#EFF6FF",
              borderWidth: 1,
              borderColor: isDark ? "#3B82F6" : "#BFDBFE",
            }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons name="tv-outline" size={20} color="#3B82F6" />
              <Text className="ml-2 font-semibold" style={{ color: isDark ? "#93C5FD" : "#1E40AF" }}>
                Live Quiz Modus
              </Text>
            </View>
            <Text className="text-sm" style={{ color: isDark ? "#60A5FA" : "#3B82F6" }}>
              Deze quiz wordt live gespeeld met teams. Jij controleert de quiz 
              vanaf je telefoon terwijl vragen op een groot scherm worden getoond.
            </Text>
          </View>

          {/* Quick Actions */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
              Snelle acties
            </Text>
            <View className="flex-row">
              <TouchableOpacity
                onPress={() => router.push(`/(admin)/games/quiz/${id}/questions`)}
                className="flex-1 rounded-xl p-4 mr-2 items-center"
                style={{ backgroundColor: `${color}15`, borderWidth: 1, borderColor: color }}
              >
                <Ionicons name="help-circle" size={24} color={color} />
                <Text className="mt-2 font-medium" style={{ color: colors.text }}>
                  Vragen beheren
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => router.push(`/(admin)/games/quiz/control?quizId=${id}` as any)}
                className="flex-1 rounded-xl p-4 ml-2 items-center"
                style={{ backgroundColor: "#10B98115", borderWidth: 1, borderColor: "#10B981" }}
              >
                <Ionicons name="play" size={24} color="#10B981" />
                <Text className="mt-2 font-medium" style={{ color: colors.text }}>
                  Start Quiz
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <View className="h-24" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
